import { type Me, type Permission, routes } from '@pmai/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ApiError, call, setCsrfToken } from '../api/client';
import { queryKeyFor } from '../api/hooks';

export const ME_KEY = queryKeyFor(routes.auth.me);

/** "Who am I?" — `null` when signed out. The one source of user, workspace and permissions. */
export function useSession() {
  return useQuery<Me | null, ApiError>({
    queryKey: ME_KEY,
    queryFn: () =>
      call(routes.auth.me, { anonymousOk: true }).catch((error: unknown) => {
        if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') return null;
        throw error;
      }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** The signed-in user inside the app shell (the root layout guarantees a session). */
export function useMe(): Me {
  const { data } = useSession();
  if (!data) throw new Error('useMe() used outside the authenticated app');
  return data;
}

export function usePermissions(): readonly Permission[] {
  return useSession().data?.permissions ?? [];
}

/** Renders `children` only with the permission (ADR 0004) — UI mirror of `@RequirePerm`. */
export function Can({
  perm,
  children,
  fallback = null,
}: {
  perm: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return usePermissions().includes(perm) ? children : fallback;
}

/**
 * A new session or a new active workspace: the new `me` is cached and every other cached query
 * is dropped, so no data of the previous user/workspace is ever shown (FR-WS-004/005).
 */
export function useApplySession() {
  const queryClient = useQueryClient();
  return (me: Me | null) => {
    setCsrfToken(me?.csrfToken ?? null);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== routes.auth.me.path });
    queryClient.setQueryData(ME_KEY, me);
  };
}
